// src/components/AttendanceShell/AttendanceShell.tsx
import React, { useEffect, useState } from "react";
import { ZeusButton, ZeusIcon, ZeusIconButton, ZeusTypography } from "zeus-front-design-system";
import LogoZeus from "../../assets/icons/logo-zeus.svg";
import * as styles from "./AttendanceShell.module.scss";
import capitalize from "../../utils/capitalize";
import Close from "../../assets/icons/exit.svg";
import { useAuth } from "../../core/auth";
import { confirm } from "../../services/confirmServices";

type TimeFormat = "12h" | "24h";

interface AttendanceShellProps {
  /** Cabecera principal (puede incluir saludo, nombre, etc) */
  header: React.ReactNode;
  /** Subtítulo bajo el header */
  subtitle?: React.ReactNode;
  /** Mensaje de ayuda opcional (texto extra) */
  helpNode?: React.ReactNode;
  /** Contenido principal (formulario, botones, etc) */
  children: React.ReactNode;

  /** Mostrar botón de regreso en el header */
  showBackButton?: boolean;
  onBack?: () => void;

  /** Formato de hora: 12h o 24h */
  timeFormat?: TimeFormat;

  /** Permite customizar el aria-labelledby*/
  ariaLabelledBy?: string;
  dividerText?: string;
  isLoagout?: boolean;
}

const AttendanceShell: React.FC<AttendanceShellProps> = ({
  header,
  subtitle = "Realiza el registro de hoy.",
  helpNode,
  children,
  showBackButton = false,
  onBack,
  timeFormat = "24h",
  ariaLabelledBy = "control-asistencia-title",
  dividerText,
  isLoagout = false,
}) => {
  const [now, setNow] = useState(new Date());
  const { logout } = useAuth();

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const dateParts = new Intl.DateTimeFormat("es-MX", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).formatToParts(now);

  const dayName = dateParts.find((p) => p.type === "weekday")?.value ?? "";
  const day = dateParts.find((p) => p.type === "day")?.value ?? "";
  const month = dateParts.find((p) => p.type === "month")?.value ?? "";
  const year = dateParts.find((p) => p.type === "year")?.value ?? "";

  const formattedDate = `${capitalize(dayName)} ${day} de ${month} de ${year}`;

  const timeFmt = new Intl.DateTimeFormat("es-MX", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: timeFormat === "12h",
  }).format(now);

  const handleLogout = () => {
    confirm(
      {
        title: "¿Deseas cerrar sesión?",
        subtitle:
          "Al realizar esta acción, el administrador tendrá que volver a iniciar sesión para ingresar al registro de asistencia.",
        confirmText: "Cerrar sesión",
        cancelText: "Cancelar",
      },
      () => {
        logout();
      }
    );
  };

  return (
    <section className={styles.cardContainer} aria-labelledby={ariaLabelledBy}>
      <div className={styles.logoContainer}>
        <div className={styles.logoHeader}>
          <LogoZeus className={styles.logo} />

          {isLoagout && (
            <button
              className={styles.logout}
              children={<Close />}
              onClick={() => handleLogout()}
            ></button>
          )}
        </div>
        {showBackButton && (
          <ZeusIconButton
            className={styles.backBtn}
            icon="undo"
            onClick={() => onBack && onBack()}
            aria-label="Regresar"
          />
        )}
      </div>

      <div className={styles.card} aria-labelledby={ariaLabelledBy}>
        <div className={styles.headerBlock}>
          {header}
          {subtitle && (
            <ZeusTypography component="p" type="Body" size="small" className={styles.subtitle}>
              {subtitle}
            </ZeusTypography>
          )}
        </div>

        <div className={styles.infoList}>
          <div className={styles.infoItem}>
            <ZeusIcon icon="calendar_month" style={{ color: "var(--brand)" }} />
            <ZeusTypography component="span" type="Body" size="small">
              {formattedDate}
            </ZeusTypography>
          </div>
          <div className={styles.infoItem}>
            <ZeusIcon icon="schedule" style={{ color: "var(--brand)" }} />
            <ZeusTypography component="span" type="Body" size="small">
              {timeFmt} {timeFormat !== "12h" ? "h." : ""}
            </ZeusTypography>
          </div>
        </div>

        {dividerText && (
          <div className={styles.divider}>
            <span className={styles.dividerText}>{dividerText}</span>
          </div>
        )}

        {helpNode}

        <div className={styles.form}>{children}</div>
      </div>
    </section>
  );
};

export default AttendanceShell;
